import settings from 'settings'
import React, { useReducer, useEffect, useMemo, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'helpers/proptypes'
import { useSelector, useDispatch } from 'react-redux'
import { useHasLoadingSucceeded } from 'hooks'
import { konstants } from '@vizeat/helpers'
import { Segment, Button, List, Form, TextArea, Rating, Header, Container, Modal } from 'semantic-ui-react'
import { fetchUserIntercomId, updateHostApplication } from 'redux/entities/actions'
import { getUser, updatingHostApplication, getUpdatingHostApplicationError } from 'redux/entities/selectors'
import { getAdminProfile } from 'redux/reducers/admin/selectors'
import { getColorFromStatus } from 'helpers/hostApplications'
import { isInvalid } from 'helpers/forms'
import { merge, omitBy } from 'lodash'
import ApiErrorMessage from 'components/errors/ApiErrorMessage'
import { AssignmentDropdown } from '../AssignmentDropdown'

const ACTIONS = {
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
  COMMENT: 'COMMENT',
  ADD_NOTE: 'ADD_NOTE',
  RESET: 'RESET',
}

function useReducerState() {
  const { t } = useTranslation()

  const initialState = useMemo(
    () => ({
      comment: '',
      modalHeader: t('HostApplications::Summary::Are you sure?'),
      modalConfirmButton: t('HostApplications::Summary::Confirm'),
      modalConfirmButtonColor: 'blue',
      isConfirmModalOpen: false,
      action: '',
      actionText: '',
      status: '',
    }),
    [t],
  )

  const [state, dispatch] = useReducer(reducer, initialState)

  function reducer(state, action) {
    switch (action.type) {
      case ACTIONS.APPROVE:
        return {
          ...state,
          isConfirmModalOpen: true,
          status: 'approved',
          action: 'approval',
          actionText: t('HostApplications::Summary::approved the application'),
          modalHeader: t('HostApplications::Summary::Are you sure?'),
          modalConfirmButtonColor: getColorFromStatus(konstants.HOST_APPLICATION_STATUSES.APPROVED),
          modalConfirmButton: t('HostApplications::Summary::Approve'),
        }
      case ACTIONS.REJECT:
        return {
          ...state,
          isConfirmModalOpen: true,
          status: 'rejected',
          action: 'rejection',
          actionText: t('HostApplications::Summary::rejected the application'),
          modalHeader: t('HostApplications::Summary::Are you sure?'),
          modalConfirmButtonColor: getColorFromStatus(konstants.HOST_APPLICATION_STATUSES.REJECTED),
          modalConfirmButton: t('HostApplications::Summary::Reject'),
        }
      case ACTIONS.COMMENT:
        return {
          ...state,
          isConfirmModalOpen: true,
          action: konstants.HOST_APPLICATION_FOLLOWUP_ACTIONS.COMMENT,
          actionText: t('HostApplications::Summary::added a comment'),
          modalHeader: t('HostApplications::Summary::Team comment'),
          modalConfirmButton: t('HostApplications::Summary::Add comment'),
          modalConfirmButtonColor: 'blue',
        }
      case ACTIONS.ADD_NOTE:
        return { ...state, comment: action.value }
      case ACTIONS.RESET:
        return initialState
    }
  }

  return { state, dispatch }
}

export function CommunitySection({ hostApplication }) {
  const { t } = useTranslation()
  const reducerDispatch = useDispatch()
  const { state, dispatch: reactDispatch } = useReducerState()

  const applicantId = hostApplication.metadata.get('user_id')
  const applicant = useSelector((state) => getUser(state, { id: applicantId }))

  const adminProfile = useSelector(getAdminProfile)

  const isUpdatingApplication = useSelector((state) => updatingHostApplication(state, hostApplication.id))
  const updateApplicationError = useSelector((state) => getUpdatingHostApplicationError(state, hostApplication.id))
  const isApplicationUpdated = useHasLoadingSucceeded(isUpdatingApplication, updateApplicationError)

  const intercomURL = useMemo(() => {
    const id = applicant.get('intercom_user_id')
    if (!id) return
    return `https://app.intercom.com/a/apps/${settings.intercomApiId}/users/${id}/all-conversations`
  }, [applicant])

  useEffect(() => {
    if (isApplicationUpdated) reactDispatch({ type: ACTIONS.RESET })
  }, [isApplicationUpdated, reactDispatch])

  useEffect(() => {
    if (applicantId) reducerDispatch(fetchUserIntercomId(applicantId))
  }, [applicantId, reducerDispatch])

  function handleActionWithFollowup({ payload, action, actionText, comment }) {
    const applicationPayload = omitBy(
      merge(
        {
          followup: {
            user_id: adminProfile.user,
            user_fullname: adminProfile.fullname,
            action,
            actionText,
            comment: comment || undefined,
          },
        },
        payload,
      ),
      isInvalid,
    )

    reducerDispatch(
      updateHostApplication(hostApplication.id, {
        ...applicationPayload,
        ...(state.status && { metadata: { ...hostApplication.metadata?.toJS(), status: state.status } }),
      }),
    )
  }

  function handleRate(_, { rating }) {
    handleActionWithFollowup({
      payload: { admin: { application_rate: rating } },
      action: 'rating',
      actionText: t('HostApplications::Summary::rated the application'),
    })
  }

  function handleContactTheHost() {
    handleActionWithFollowup({
      action: konstants.HOST_APPLICATION_FOLLOWUP_ACTIONS.CONTACT,
      actionText: t('HostApplications::Summary::contacted the host'),
    })
    if (intercomURL) window.open(intercomURL)
  }

  if (!hostApplication) return null

  return (
    <Segment>
      <Header as='h3'>{t('HostApplications::Summary::Actions')}</Header>
      <Form>
        <List relaxed>
          <List.Item>
            <Form.Field>
              <label>{t('HostApplications::Summary::Rate this application:')}</label>
              <Rating
                icon='star'
                maxRating={5}
                defaultRating={hostApplication.admin.get('application_rate')}
                onRate={handleRate}
              />
            </Form.Field>
          </List.Item>
          <List.Item>
            <Form.Field>
              <label>{t('HostApplications::Summary::Assign to:')}</label>
              <AssignmentDropdown hostApplication={hostApplication} />
            </Form.Field>
          </List.Item>
          <List.Item>
            {[konstants.HOST_APPLICATION_STATUSES.TO_REVIEW, konstants.HOST_APPLICATION_STATUSES.RE_SUBMITTED].includes(
              hostApplication.metadata.get('status'),
            ) && (
              <Fragment>
                <Button
                  style={{ marginTop: '5px' }}
                  floated='left'
                  color={getColorFromStatus(konstants.HOST_APPLICATION_STATUSES.APPROVED)}
                  onClick={() => reactDispatch({ type: ACTIONS.APPROVE })}
                >
                  {t('HostApplications::Summary::Approve')}
                </Button>

                <Button
                  style={{ marginTop: '5px' }}
                  floated='left'
                  color={getColorFromStatus(konstants.HOST_APPLICATION_STATUSES.REJECTED)}
                  onClick={() => reactDispatch({ type: ACTIONS.REJECT })}
                >
                  {t('HostApplications::Summary::Reject')}
                </Button>

                <Button floated='left' color='orange' style={{ marginTop: '5px' }} onClick={handleContactTheHost}>
                  {intercomURL
                    ? t('HostApplications::Summary::Contact the host & open Intercom')
                    : t('HostApplications::Summary::Contact the host')}
                </Button>
              </Fragment>
            )}
            <Button
              style={{ marginTop: '5px' }}
              floated='left'
              color='blue'
              onClick={() => reactDispatch({ type: ACTIONS.COMMENT })}
            >
              {t('HostApplications::Summary::Team Comment')}
            </Button>
          </List.Item>
        </List>
      </Form>

      <Modal open={state.isConfirmModalOpen} onClose={() => reactDispatch({ type: ACTIONS.RESET })} size='small'>
        <Header content={state.modalHeader} />
        <Modal.Content>
          <Container>
            <Form>
              <Form.Field>
                <label>{t('HostApplications::Summary::Message to the candidate:')}</label>
                <TextArea
                  placeholder={t('HostApplications::Summary::Note from the CM')}
                  onChange={({ target }) => reactDispatch({ type: ACTIONS.ADD_NOTE, value: target.value })}
                  value={state.comment}
                />
              </Form.Field>
            </Form>
          </Container>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => reactDispatch({ type: ACTIONS.RESET })}>Cancel</Button>
          <Button
            color={state.modalConfirmButtonColor}
            onClick={() =>
              handleActionWithFollowup({
                action: state.action,
                actionText: state.actionText,
                comment: state.comment,
              })
            }
          >
            {state.modalConfirmButton}
          </Button>
        </Modal.Actions>
      </Modal>

      <ApiErrorMessage error={updateApplicationError} modal />
    </Segment>
  )
}

CommunitySection.propTypes = {
  hostApplication: PropTypes.immutable.record,
}

CommunitySection.defaultProps = {
  hostApplication: undefined,
}
